import React from "react";
import moment from "moment";
import type { ApolloClient } from "@apollo/client";
import Alert from "components/Alert";
import PayrollStatus from "components/PayrollStatus";
import Button from "components/Button";
import IconTable from "components/IconTable";
import type { IconTableColumn } from "components/IconTable";

import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { connect, type ConnectedProps } from "react-redux";
import { push } from "store/actions/router";
import { get, keyBy, map } from "lodash";
import { Card, Form } from "react-bootstrap";
import { FiSettings } from "react-icons/fi";
import { currentPayrolls, getAllEmployerGroups } from "actions/employerActions";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { employerConstants } from "actions/types";
import { getPeriodState } from "store/selectors/payrolls";
import { banksSelector } from "store/selectors/bank";
import { doesBankNeedReAuth } from "utils/bankReAuth";
import type { RootState } from "types/state";

import "./EmployerDashboard.scss";

const allGroups = {
  id: "ALL_GROUPS",
  value: "ALL_GROUPS",
  name: "All Groups",
};

const commonDateFormat = "MMM Do YYYY";

const updatingActions = [employerConstants.GET_EMPLOYER_GROUPS];

const errorSelector = createErrorSelector(updatingActions);
const isUpdating = createLoadingSelector(updatingActions);

const mapStateToProps = (state: RootState) => {
  return {
    periodState: getPeriodState(state),
    groupsById: keyBy(state.employer.groups, "id"),
    payroll: state.employer.currentPayrolls,
    groups: state.employer.groups,
    isUpdating: isUpdating(state),
    error: errorSelector(state),
    banksNeedingReAuth: banksSelector(state),
  };
};

const mapDispatchToProps = {
  push,
  getAllEmployerGroups,
  currentPayrolls,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = WithApolloClient<PropsFromRedux>;

interface State {
  // set in the constructor but never read or rewritten — preserved as-is
  initialFetching: boolean;
  selectedGroupFilter: string;
}

/** Fields of a payroll row that the table columns read. */
interface PayrollRow extends Record<string, unknown> {
  groupId?: string;
  payPeriodStart?: string;
  payPeriodEnd?: string;
  payrollProcessingDate?: string;
  periodState?: string;
  status?: string;
}

class CurrentPayrollTable extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      initialFetching: true,
      selectedGroupFilter: "ALL_GROUPS",
    };
  }

  componentDidMount() {
    // Pre-existing behavior: currentPayrolls is called with only the client,
    // omitting the required companyId argument (passes undefined at runtime).
    // @ts-expect-error preserve the original single-argument call (missing companyId)
    void this.props.currentPayrolls(this.props.client as ApolloClient<object>);
  }

  _createGroupFilter = (): Array<{
    id?: string;
    value: unknown;
    name: unknown;
  }> => {
    const groups = this.props.groups.map((group) => {
      return { value: group.name, name: group.name };
    });
    return [allGroups, ...groups];
  };

  _filteredGroups = () => {
    const allGroups = this.props.payroll as PayrollRow[];
    if (this.state.selectedGroupFilter === "ALL_GROUPS") return allGroups;

    return allGroups.filter((payroll) => {
      const groupName = get(this.props.groupsById[payroll.groupId!], "name");
      return groupName === this.state.selectedGroupFilter;
    });
  };

  _buildColumns = (): Array<IconTableColumn<PayrollRow>> => {
    const columns: Array<IconTableColumn<PayrollRow>> = [
      {
        label: "Group Name",
        customComponent: (props) => {
          const groupName = get(this.props.groupsById[props.groupId!], "name");
          return groupName as React.ReactNode;
        },
      },

      {
        label: "Payroll Period",
        customComponent: (props) => {
          const start = props.payPeriodStart;
          const end = props.payPeriodEnd;
          const period = `${moment(start).format(commonDateFormat)} - ${moment(
            end
          ).format(commonDateFormat)}`;
          return period;
        },
      },
      {
        label: "Processing Date",
        customComponent: (props) => {
          const payrollProcessingDate = props.payrollProcessingDate;
          return moment(payrollProcessingDate).format(commonDateFormat);
        },
      },

      {
        label: "Status",
        customComponent: (props) => {
          const groupId = props.groupId;
          const group = this.props.groupsById[groupId!];
          const associatedBankAccountId = get(
            group,
            "associatedBankAccount.id"
          );
          const needsReAuth = doesBankNeedReAuth(
            this.props.banksNeedingReAuth as Parameters<
              typeof doesBankNeedReAuth
            >[0],
            associatedBankAccountId as string | null | undefined
          );

          let periodState;
          if (needsReAuth) {
            periodState = "RE_AUTH_REQUIRED";
          } else {
            periodState = props.periodState;
          }
          return (
            <PayrollStatus periodState={periodState!} status={props.status!} />
          );
        },
      },
    ];
    return columns;
  };

  render() {
    const title = "Payroll Contributions";

    const columns = this._buildColumns();
    const data = this._filteredGroups();

    return (
      <Card className="process-contributions">
        <div className="widget-header">
          <span>
            <Card.Title>{title}</Card.Title>
            <Card.Text>
              <span>
                <Form.Select
                  className="group-filter"
                  id="inlineFormCustomSelect"
                  value={this.state.selectedGroupFilter}
                  onChange={(e) => {
                    this.setState({
                      selectedGroupFilter: e.target.value,
                    });
                  }}
                >
                  {map(this._createGroupFilter(), (group) => (
                    <option value={group.value as string} key={group.id}>
                      {group.name as React.ReactNode}
                    </option>
                  ))}
                </Form.Select>
              </span>
            </Card.Text>
          </span>
          <Button
            size="sm"
            name="action"
            color="action"
            icon={{
              icon: <FiSettings size={14} stroke={"#29b0c2"} />,
              position: "left",
            }}
            onClick={() => {
              this.props.push(`/dashboard/contributions`);
            }}
            btnLabel="Manage Payroll"
          />
        </div>
        {this.props.error && <Alert type="error" msg={this.props.error} />}
        <IconTable columns={columns} data={data} />
      </Card>
    );
  }
}

export default connector(withApollo<Props>(CurrentPayrollTable));
